import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import DesignItem from '@/components/DesignItem';
import {
	useGetProductListMutation,
	useGetFilterOptionsMutation
} from '@/features/my-dashborad/api/myDashboardApi';
import MyDashboardTopbar from '@/features/my-dashborad/components/MyDashboardTopbar';
import MyDashboardListView from '@/features/my-dashborad/components/MyDashboardListView';
import LoaderComponent from '@/components/LoaderComponent';
import { storeProductData } from '@/store/actions/DesignAction';
import { useDownloadTpSummaryMutation } from '@/api/commonDownloadsApi';
import PublishDesign from '@/features/collection/components/PublishDesign';
import BottomPopup from '@/features/my-dashborad/components/BottomPopup';
import { clearDesignViewModalInfo, setDesignViewModalInfo } from '@/store/actions/DesignViewModalAction';
import { toastError, toastSuccess } from '@/components/Toast';
import { likeProduct } from '@/store/design/action';
import { useLikeProductMutation } from '@/features/Scan/api/scanApi';
import SelectFolderPopup from '@/features/favourites/components/SelectFolderPopup';
import FilterComponent from '@/features/filter/v2/FilterComponent';
import {
	allFilterPages,
	designFieldList,
	generateProductListParams
} from '@/features/filter/static/filterUtil';
import { dashboardGroupBy } from '@/features/my-dashborad/utils';
import { DASHBOARD_GROUP_BY_IDS } from '@/features/task-configuration/constants';
import { useGetTaskDashboardFilterByIdQuery } from '@/features/task-configuration/api/taskConfigurationApi';
import { setSystemUserPreferences } from '@/services/commonService';
import { getUserPreference } from '@/utils/Util';

const MyDashboardStyles = () => {
	const userPreference = getUserPreference();
	const [searchParams] = useSearchParams();
	const [searchKey, setSearchKey] = useState('');
	const [selectedPriority, setSelectedPriority] = useState(
		userPreference?.DASHBOARD_PRIORITY_OPTION || null
	);

	// Derive whether the selected option is a group-by type
	const isGroupByOption = DASHBOARD_GROUP_BY_IDS.includes(selectedPriority?.id);
	const selectedGroupBy = isGroupByOption ? selectedPriority : null;
	const [selectedProducts, setSelectedProducts] = useState([]);
	const [listViewDesignList, setListViewDesignList] = useState(null);
	const [page, setPage] = useState(0);
	const [allDesigns, setAllDesigns] = useState([]);
	const [hasMore, setHasMore] = useState(true);
	const [getProductList, { isLoading: poductsLoading, data: designList = {} }] = useGetProductListMutation({
		params: { page: 0, size: 20, sort: 'id,desc' }
	});
	const [getFilterOptions] = useGetFilterOptionsMutation();
	const [downloadTpSummary] = useDownloadTpSummaryMutation();
	const [likeProductMutation] = useLikeProductMutation();
	const observerTarget = useRef(null);
	const isLoadingRef = useRef(false);
	const [isShowFilter, setIsShowFilter] = useState(false);

	const [isShowPublishDesign, setIsShowPublishDesign] = useState(false);
	const [isUnSelectAll, setIsUnSelectAll] = useState(false);
	const [isShowFavouriteFolderPopup, setIsShowFavouriteFolderPopup] = useState(false);
	const [currentLikedStyle, setCurrentLikedStyle] = useState(null);
	const [isShowCreateNew, setIsShowCreateNew] = useState(false);
	const dispatch = useDispatch();
	const filterfields = useSelector((store) => store.filterFieldStore);
	const [selectedViewType, setSelectedViewType] = useState(
		userPreference?.MY_DASHBOARD_VIEW_TYPE || 'GRID'
	);

	// Get filter ID from URL query params - single source of truth
	const filterId = searchParams.get('filterId');
	const filterTitle = searchParams.get('filterTitle') || '';
	const listViewType = searchParams.get('listViewType');

	const { data: taskDashboardFilterById } = useGetTaskDashboardFilterByIdQuery(
		{ id: filterId },
		{ skip: !filterId }
	);
	const listViewTypeFromFilter =
		taskDashboardFilterById?.listViewType?.constant ?? taskDashboardFilterById?.listViewType ?? '';
	const mergeListViewType = listViewType || listViewTypeFromFilter || '';

	const onCloseViewModal = () => {
		dispatch(clearDesignViewModalInfo({}));
	};

	const handleClickDesignImage = (item) => {
		dispatch(
			setDesignViewModalInfo({
				id: item?.id,
				isShowMergeTechPack: item?.isSet,
				onCloseViewModal: onCloseViewModal,
				source: 'PRODUCT_LIST'
			})
		);
	};

	const handleSuccessLikeProduct = () => {
		!isShowFavouriteFolderPopup && setIsShowFavouriteFolderPopup(true);
		toastSuccess('Added to favourite');
	};

	const onSetLike = (id) => {
		const productList = designList?.data.map((item) => {
			if (item.id === id) {
				return {
					...item,
					liked: true
				};
			}
			return item;
		});
		setAllDesigns(productList);
	};

	const handleLikeProduct = (designId) => {
		onSetLike(designId);
		setIsShowFavouriteFolderPopup(true);
		dispatch(likeProduct(designId, handleSuccessLikeProduct));
		setCurrentLikedStyle(designId);
	};

	const handleProductUpdate = () => {};

	const loadMoreProducts = useCallback(async () => {
		if (selectedViewType !== 'GRID' || isLoadingRef.current || !hasMore) return;

		isLoadingRef.current = true;

		try {
			const filterBody = generateProductListParams(filterfields);
			const body = {
				...filterBody,
				...(searchKey && { referenceNumber: searchKey }),
				...(selectedPriority?.id && { searchSortingOption: selectedPriority.id }),
				...(filterId && { taskDashboardFilterId: parseInt(filterId) })
			};

			const result = await getProductList({
				body,
				params: { page, size: 20, sort: 'id,desc' }
			}).unwrap();

			if (result?.data) {
				setAllDesigns((prevDesigns) => [...prevDesigns, ...result.data]);
				setPage((prevPage) => prevPage + 1);

				// Check if there are more pages
				const totalPages = result.totalPages || 0;
				if (page + 1 >= totalPages) {
					setHasMore(false);
				}
			}
		} catch (error) {
			toastError(error?.data?.message || 'Something went wrong!');
			setHasMore(false);
		} finally {
			isLoadingRef.current = false;
		}
	}, [
		page,
		searchKey,
		selectedPriority,
		filterId,
		hasMore,
		getProductList,
		filterfields,
		selectedViewType
	]);

	// Reset pagination when filters change
	useEffect(() => {
		setPage(0);
		setAllDesigns([]);
		setHasMore(true);
		isLoadingRef.current = false;
	}, [searchKey, selectedPriority, filterId, filterfields]);

	// Initial load and reload on search/filter change (grid only — list view uses getListViewResponse)
	useEffect(() => {
		if (selectedViewType !== 'GRID') return;
		if (page === 0 && allDesigns.length === 0) {
			loadMoreProducts();
		}
	}, [page, allDesigns.length, loadMoreProducts, selectedViewType]);

	// Infinite scroll observer (grid only)
	useEffect(() => {
		if (selectedViewType !== 'GRID') return;

		const observer = new IntersectionObserver(
			(entries) => {
				if (
					selectedViewType === 'GRID' &&
					entries[0].isIntersecting &&
					hasMore &&
					!isLoadingRef.current
				) {
					loadMoreProducts();
				}
			},
			{ threshold: 1.0 }
		);

		const target = observerTarget.current;
		if (target) {
			observer.observe(target);
		}

		return () => {
			if (target) {
				observer.unobserve(target);
			}
		};
	}, [hasMore, loadMoreProducts, selectedViewType]);

	const handleUpdateDesignOnPublish = (productIds) => {
		const updatedProductList = allDesigns?.map((product) => {
			if (productIds.includes(product.id)) {
				return {
					...product,
					productStatusList: product.productStatusList.map((status) => ({
						...status,
						entity: 'DESIGN',
						status: 'Published'
					}))
				};
			}
			return product;
		});
		setAllDesigns(updatedProductList);
	};

	const handleSelectAll = () => {
		const activeDesigns = selectedViewType === 'LIST' ? listViewDesignList?.data || [] : allDesigns || [];
		const designIds = activeDesigns?.map((product) => product?.id);
		setSelectedProducts((prevSelected) => [...new Set([...prevSelected, ...designIds])]);
	};

	const onActionClick = (type) => {
		switch (type) {
			case 'SELECT_ALL':
				setIsUnSelectAll(true);
				handleSelectAll();

			default:
				break;
		}
	};

	const getRemainingStyles = () => {
		const totalElements =
			selectedViewType === 'LIST'
				? listViewDesignList?.totalElements || listViewDesignList?.data?.length || 0
				: allDesigns?.length || 0;
		const selectedCount = selectedProducts?.length || 0;
		const remaining = totalElements - selectedCount;

		return remaining < 30 ? remaining : 30;
	};

	const downloadReportByProductIds = () => {
		downloadTpSummary({
			productIds: selectedProducts
		})
			.unwrap()
			.then(() => {});
	};

	const handleDownload = (type) => {
		if (type === 'DOWNLOAD_REPORT') {
			downloadReportByProductIds();
		}
	};

	const clearSelection = () => {
		setSelectedProducts([]);
	};

	const handleGetFilterOptions = useCallback(() => {
		let requestBody = generateProductListParams(filterfields);
		getFilterOptions({
			params: {},
			body: requestBody
		});
	}, [filterfields, getFilterOptions]);

	// Fetch updated filter options whenever filterfields change and filter panel is open
	useEffect(() => {
		if (!isShowFilter) return;
		handleGetFilterOptions();
	}, [filterfields, isShowFilter]);

	const handlePriorityChange = async (value) => {
		setSelectedPriority(value);
		await setSystemUserPreferences({ DASHBOARD_PRIORITY_OPTION: value });
	};

	useEffect(() => {
		(async () => {
			await setSystemUserPreferences({
				...userPreference,
				MY_DASHBOARD_VIEW_TYPE: selectedViewType
			});
		})();
	}, [selectedViewType]);

	// Group designs by selected group-by option
	const groupedData = useMemo(() => {
		if (!selectedGroupBy?.id) return null;
		return dashboardGroupBy(allDesigns, selectedGroupBy.id);
	}, [allDesigns, selectedGroupBy]);

	const handleBulkFavourite = () => {
		likeProductMutation({
			productIds: selectedProducts,
			folderId: userPreference?.DEFAULT_FAVOURITE_FOLDER?.id || -1,
			responseType: 'SCAN'
		});
		setCurrentLikedStyle(selectedProducts);
		setIsShowFavouriteFolderPopup(true);
	};

	const handleCopyToFolder = (selectedFolder, handleSuccess) => {
		likeProductMutation({
			productIds: selectedProducts,
			folderId: selectedFolder?.id,
			responseType: 'SCAN'
		})
			.unwrap()
			.then(() => {
				handleSuccess(selectedFolder);
				setSelectedProducts([]);
			});
	};

	const renderDesignList = (designs) => {
		return designs?.map((design, index) => (
			<DesignItem
				design={design}
				index={index}
				selectedProducts={selectedProducts}
				setSelectedProducts={setSelectedProducts}
				onClickDesignImage={() => handleClickDesignImage(design)}
				key={`design_${design.id}_${index}`}
				onLikeProduct={() => handleLikeProduct(design?.id)}
				fromPage={'DESIGN_PAGE'}
				isMyDesignTabSelected={false}
				isShowPrice={false}
				isFromNitexStudio={false}
				offerPriceInfo={[]}
				handleProductUpdate={handleProductUpdate}
				failedTaskUpdateIds={[]}
			/>
		));
	};

	const renderGroupedData = () => {
		if (!groupedData) return null;
		return Object.keys(groupedData).map((groupName, index) => (
			<div className="favorite-collection-container p-0 bg-white" key={`group_${index}`}>
				<span className="semibold-16 mr-2">
					{groupName} {`(${groupedData[groupName]?.length})`}
				</span>
				<div className="design-list-container favourite-list-container mt-3">
					<div className="design-list-row">{renderDesignList(groupedData[groupName])}</div>
				</div>
			</div>
		));
	};

	return (
		<LoaderComponent loading={selectedViewType === 'GRID' && (poductsLoading || designList.isLoading)}>
			<MyDashboardTopbar
				onSearch={(value) => setSearchKey(value)}
				searchKey={searchKey}
				onSelectPriority={handlePriorityChange}
				selectedPriority={selectedPriority}
				selectedProducts={selectedProducts}
				setSelectedProducts={setSelectedProducts}
				filterTitle={filterTitle}
				designCount={
					selectedViewType === 'LIST'
						? (listViewDesignList?.totalElements ?? listViewDesignList?.data?.length ?? 0)
						: designList?.totalElements
				}
				toggleFilter={() => {
					handleGetFilterOptions();
					setIsShowFilter((prev) => !prev);
				}}
				allDesigns={allDesigns}
				selectedViewType={selectedViewType}
				setSelectedViewType={setSelectedViewType}
			/>

			<section className="bg-white filter-data-container filter-v3-data-container py-0">
				<FilterComponent
					filterPageName={allFilterPages.PRODUCT_LIST}
					fields={designFieldList}
					showFilter={isShowFilter}
					isDependentFilter={true}
				/>
				<div className={`data-table ${selectedViewType === 'LIST' ? 'scroll-x-label' : ''}`}>
					{selectedViewType === 'GRID' && (
						<div className="favorite-collection-wrapper">
							{groupedData ? (
								renderGroupedData()
							) : (
								<div className="favorite-collection-container p-0 bg-white">
									<div className="design-list-container favourite-list-container">
										<div className="design-list-row">{renderDesignList(allDesigns)}</div>
									</div>
								</div>
							)}
							{/* Infinite Scroll Trigger */}
							<div ref={observerTarget} style={{ height: '20px', margin: '20px 0' }}></div>
						</div>
					)}

					{selectedViewType === 'LIST' && (
						<MyDashboardListView
							key={`${filterId || ''}_${mergeListViewType || ''}`}
							filterId={filterId}
							filterTitle={filterTitle}
							listViewType={mergeListViewType}
							selectedProducts={selectedProducts}
							setSelectedProducts={setSelectedProducts}
							onListDataUpdate={setListViewDesignList}
							searchKey={searchKey}
							selectedPriority={selectedPriority}
						/>
					)}
				</div>
			</section>
			{/* Bottom Popup */}
			{selectedProducts?.length > 0 && (
				<BottomPopup
					selectedProducts={selectedProducts}
					designList={
						selectedViewType === 'LIST'
							? {
									totalElements:
										listViewDesignList?.totalElements ||
										listViewDesignList?.data?.length ||
										0,
									data: listViewDesignList?.data || []
								}
							: { ...designList, data: allDesigns }
					}
					onActionClick={onActionClick}
					isUnSelectAll={isUnSelectAll}
					getRemainingStyles={getRemainingStyles}
					setIsShowPublishDesign={setIsShowPublishDesign}
					handleDownload={handleDownload}
					clearSelection={clearSelection}
					onBulkFavourite={handleBulkFavourite}
				/>
			)}

			{/** Modals */}
			{isShowPublishDesign && (
				<PublishDesign
					selectedProducts={selectedProducts}
					onClose={() => {
						dispatch(storeProductData('selectedProductIds', []));
						setIsShowPublishDesign(false);
					}}
					onSubmitSuccess={handleUpdateDesignOnPublish}
				/>
			)}
			<div className={`${isShowFavouriteFolderPopup ? 'show' : 'hide'}`}>
				<SelectFolderPopup
					onClose={() => setIsShowFavouriteFolderPopup(false)}
					isShowCreateNew={isShowCreateNew}
					setIsShowCreateNew={setIsShowCreateNew}
					currentLikedStyle={currentLikedStyle}
					{...(Array.isArray(currentLikedStyle) && {
						handleCopyToFolder,
						favFrom: 'SCAN'
					})}
				/>
			</div>
		</LoaderComponent>
	);
};

export default MyDashboardStyles;
